import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import Map from '@shared/components/Map';
import { Coord } from '@shared/interfaces/map.interface';
import { CustomCallback } from '@shared/types';

import SearchBar from '../SearchBar/SearchBar';
import { searchTextAtomFamily } from '../SearchBar/state/searchBar.atom';

export const SEARCH_TEXT_ID = 'addressSearcher';

interface ISearchedItem {
  id: string;
  name: string;
  address: string;
  roadAddress: string;
  x: string;
  y: string;
}

interface IProps {
  onChange: CustomCallback<{ address: string; coord?: Coord }, any>;
  placeholder?: string;
  selectedLocationLabel?: string;
  compact?: boolean;
  //* 고른 위치를 부모가 직접 보여줄 때 지도·표기주소를 그리지 않는다
  hideSelectedPreview?: boolean;
}

const AddressSearcher: FC<IProps> = ({
  onChange,
  placeholder = '장례식장 이름 및 주소로 검색하세요',
  selectedLocationLabel = '표기주소',
  compact = false,
  hideSelectedPreview = false,
}: IProps) => {
  const searchText = useRecoilValue(searchTextAtomFamily(SEARCH_TEXT_ID));

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<ISearchedItem | null>(null);
  const [searchedItemList, setSearchedItemList] = useState<ISearchedItem[]>([]);
  const [searchStatus, setSearchStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [searchRequest, setSearchRequest] = useState(0);

  //* 마운트 시점 검색어로 초기화 → 마운트에서는 초기화 신호를 보내지 않는다
  const prevSearchTextRef = useRef<string>(searchText);

  //* 검색 실행은 검색어에만 반응한다 (onChange를 넣으면 부모 리렌더마다 카카오 검색이 재호출된다)
  useEffect(() => {
    if (!searchText.trim()) {
      setIsSearching(false);
      setSearchStatus('idle');
      setSearchedItemList([]);
      return;
    }

    let active = true;
    setSelectedItem(null);
    setIsSearching(true);
    setSearchStatus('loading');
    setSearchedItemList([]);
    const kakao = (window as any).kakao;
    try {
      const places = new kakao.maps.services.Places();
      places.keywordSearch(searchText.trim(), (result: any[], status: string) => {
        //* 새 검색이나 화면 이동 뒤에 도착한 이전 검색 결과는 표시하지 않는다.
        if (!active) return;
        if (status === kakao.maps.services.Status.OK) {
          setSearchedItemList(
            result.map((item: any) => ({
              id: item.id,
              name: item.place_name,
              address: item.address_name,
              roadAddress: item.road_address_name,
              x: item.x,
              y: item.y,
            })),
          );
          setSearchStatus('success');
        } else if (status === kakao.maps.services.Status.ZERO_RESULT) {
          setSearchStatus('success');
        } else {
          setSearchStatus('error');
        }
      });
    } catch {
      setSearchStatus('error');
    }
    return () => {
      active = false;
    };
  }, [searchText, searchRequest]);

  useEffect(() => {
    //* 선택 초기화(빈 주소)는 검색어가 실제로 바뀐 경우에만 알린다.
    //* 마운트나 부모 리렌더로 이 effect가 다시 돌 때도 알리면
    //* - 편집 폼에서는 기존 주소가 빈 값으로 덮여 저장되고
    //* - watch를 쓰는 폼에서는 setValue → 리렌더 → effect 재실행으로 무한 루프가 된다.
    if (prevSearchTextRef.current === searchText) return;
    prevSearchTextRef.current = searchText;
    setSelectedItem(null);
    onChange({ address: '', coord: undefined });
  }, [onChange, searchText]);

  const renderNoResult = useMemo(
    () => (
      <p className="whitespace-pre-line break-keep py-3 text-center text-xs font-medium">
        검색된 주소가 없습니다
      </p>
    ),
    [],
  );

  const renderSearchBox = useMemo(() => {
    const onSearchedAddressItemClick = (item: ISearchedItem) => {
      setSelectedItem(item);
      setIsSearching(false);
      onChange({
        address: item.roadAddress || item.address,
        coord: { x: item.x, y: item.y },
      });
    };

    if (searchStatus === 'loading') {
      return (
        <p role="status" className="py-2 text-center text-xs text-gray-500">
          주소를 검색하고 있어요.
        </p>
      );
    }
    if (searchStatus === 'error') {
      return (
        <p role="status" className="py-2 text-center text-xs text-gray-500">
          주소를 검색하지 못했어요. 잠시 후 다시 시도해주세요.
        </p>
      );
    }
    return searchedItemList.length > 0 ? (
      <ul className="max-h-64 space-y-2 overflow-y-auto border px-4 py-3 theme-bg-1 theme-border-1">
        {searchedItemList.map((item, index) => (
          <li key={'searched-address-item-' + item.id}>
            <button
              type="button"
              className={`w-full space-y-1.5 text-start theme-border-1 ${
                index === 0 ? '' : 'border-t pt-3'
              }`}
              onClick={() => onSearchedAddressItemClick(item)}
            >
              <h6 className="font-bold">{item.name}</h6>
              <p className="whitespace-pre-line break-keep text-xs">
                <span className="pr-2 text-xs font-medium theme-text-8">지번</span>
                {item.address}
              </p>
              <p className="whitespace-pre-line break-keep text-xs">
                <span className="pr-2 text-xs font-medium theme-text-8">도로명</span>
                {item.roadAddress}
              </p>
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <>{renderNoResult}</>
    );
  }, [onChange, renderNoResult, searchedItemList, searchStatus]);

  const renderMap = useMemo(() => {
    return selectedItem ? (
      <>
        <div
          className={
            compact ? 'h-36 w-full overflow-hidden rounded-lg' : 'h-[200px] w-full'
          }
        >
          <Map
            coord={{ x: selectedItem.x, y: selectedItem.y }}
            name={selectedItem.name}
          />
        </div>
        <div className=" space-y-1">
          <h5 className="text-sm font-medium">{selectedLocationLabel}</h5>
          <p className="whitespace-pre-line break-keep font-medium theme-text-8">
            {selectedItem.roadAddress || selectedItem.address}
          </p>
        </div>
      </>
    ) : null;
  }, [compact, selectedItem, selectedLocationLabel]);

  return (
    <div className="space-y-3">
      <SearchBar
        searchTextAtomId={SEARCH_TEXT_ID}
        placeholder={placeholder}
        onSearch={() => setSearchRequest((value) => value + 1)}
      />
      {(isSearching || (selectedItem && !hideSelectedPreview)) && (
        <div className="space-y-3">{isSearching ? renderSearchBox : renderMap}</div>
      )}
    </div>
  );
};

export default AddressSearcher;
